import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getLanding, type QrLandingResponse } from '@/lib/api';

export default function QrLanding() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; data: QrLandingResponse }
  >({ kind: 'loading' });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const data = await getLanding(id);
        setState({ kind: 'ready', data });
      } catch (e) {
        setState({ kind: 'error', message: (e as Error).message });
      }
    })();
  }, [id]);

  return (
    <div className="min-h-screen px-5 py-8 max-w-md mx-auto">
      <header className="flex justify-center mb-6">
        <img src="/logos/elixir-mono.svg" alt="Elixir" className="h-14 w-auto" />
      </header>

      {state.kind === 'loading' && (
        <div className="flex flex-col items-center justify-center py-20 text-elixir-cream/60">
          <div className="w-8 h-8 border-2 border-elixir-gold border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm">Cargando tu diagnóstico…</p>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card text-center">
          <h1 className="display text-xl mb-2">Resultado no disponible</h1>
          <p className="text-sm text-elixir-cream/65">
            Puede que el enlace haya expirado. Acércate a la tienda donde hiciste el test
            para que te lo regeneren.
          </p>
        </div>
      )}

      {state.kind === 'ready' && <Landing data={state.data} />}
    </div>
  );
}

function Landing({ data }: { data: QrLandingResponse }) {
  return (
    <div className="space-y-6 animate-fade-up pb-8">
      {/* Saludo */}
      <section className="text-center">
        {data.name && (
          <p className="text-elixir-gold text-sm uppercase tracking-widest mb-1">
            {data.name}
          </p>
        )}
        <h1 className="display text-3xl text-elixir-cream leading-tight">
          Tu diagnóstico olfativo
        </h1>
        <p className="text-sm text-elixir-cream/60 mt-2">
          Esto es lo que nuestro perfumista ha encontrado para ti
        </p>
      </section>

      {/* Cupón sticky */}
      {data.coupon && !data.coupon.redeemed && (
        <section className="card border-elixir-gold/40">
          <div className="text-center">
            <p className="text-xs text-elixir-cream/55 uppercase tracking-widest mb-1">
              Tu cupón exclusivo
            </p>
            <p className="display text-elixir-gold text-3xl mb-1">
              -{data.coupon.discount}%
            </p>
            <p className="display text-elixir-cream text-lg tracking-widest mb-3">
              {data.coupon.code}
            </p>
            <p className="text-xs text-elixir-cream/60">
              Canjeable solo en tienda física Elixir
              {data.store && (
                <>
                  {' '}— {data.store.name}
                </>
              )}
            </p>
          </div>
        </section>
      )}

      {/* Recomendaciones */}
      <section className="space-y-4">
        <h2 className="display text-xl text-elixir-cream text-center">
          Tus 3 fragancias
        </h2>

        {data.recommendations.map((r, idx) => (
          <article key={r.productId} className="card relative">
            <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-elixir-gold text-elixir-black flex items-center justify-center display text-base shadow-gold">
              {idx + 1}
            </div>
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-elixir-emerald/80 text-elixir-cream text-[10px] font-semibold">
              {r.matchPct}% match
            </div>

            <div className="flex gap-3 mt-6">
              <div className="flex-shrink-0 w-24 h-24 bg-elixir-black/40 rounded-xl2 overflow-hidden flex items-center justify-center">
                {r.imageUrl ? (
                  <img src={r.imageUrl} alt={r.title} className="w-full h-full object-contain p-2" />
                ) : (
                  <span className="text-elixir-gold/40 text-3xl">◆</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                {r.brand && (
                  <p className="text-[10px] text-elixir-gold uppercase tracking-widest">
                    {r.brand}
                  </p>
                )}
                <h3 className="display text-base text-elixir-cream leading-tight mt-0.5">
                  {r.title}
                </h3>
                {r.family && (
                  <p className="text-xs text-elixir-cream/55 capitalize mt-0.5">
                    {r.family}
                  </p>
                )}
                {r.price != null && (
                  <p className="display text-elixir-gold text-lg mt-1.5">
                    {r.price.toFixed(2)} {r.currency ?? 'EUR'}
                  </p>
                )}
              </div>
            </div>

            {r.reasons.length > 0 && (
              <ul className="text-xs text-elixir-cream/70 space-y-1 mt-3">
                {r.reasons.map((reason, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-elixir-gold">·</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            )}

            {(r.notes.top.length + r.notes.heart.length + r.notes.base.length) > 0 && (
              <details className="text-xs text-elixir-cream/55 mt-3">
                <summary className="cursor-pointer text-elixir-gold/80 select-none">
                  Pirámide olfativa
                </summary>
                <div className="mt-2 space-y-0.5">
                  {r.notes.top.length > 0 && (
                    <p><span className="text-elixir-gold/70">Salida:</span> {r.notes.top.join(', ')}</p>
                  )}
                  {r.notes.heart.length > 0 && (
                    <p><span className="text-elixir-gold/70">Corazón:</span> {r.notes.heart.join(', ')}</p>
                  )}
                  {r.notes.base.length > 0 && (
                    <p><span className="text-elixir-gold/70">Fondo:</span> {r.notes.base.join(', ')}</p>
                  )}
                </div>
              </details>
            )}
          </article>
        ))}
      </section>

      {/* CTA tienda */}
      {data.store && (
        <section className="card text-center">
          <h3 className="display text-xl text-elixir-cream mb-2">
            Ven a probarlas
          </h3>
          <p className="text-sm text-elixir-cream/70">{data.store.name}</p>
          {data.store.address && (
            <p className="text-xs text-elixir-cream/55 mt-1">{data.store.address}</p>
          )}
          {data.store.schedule && (
            <p className="text-xs text-elixir-cream/45 mt-1">{data.store.schedule}</p>
          )}
          {data.store.address && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                data.store.address + ' ' + data.store.city
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-4 text-sm"
            >
              Cómo llegar →
            </a>
          )}
        </section>
      )}

      {/* Compartir */}
      <section className="flex gap-2">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(
            `Mi diagnóstico olfativo Elixir: ${typeof window !== 'undefined' ? window.location.href : ''}`
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost flex-1 text-sm"
        >
          Compartir
        </a>
      </section>

      <footer className="text-center text-[10px] text-elixir-cream/35 pt-4">
        elixirperfumes.es · Diagnóstico olfativo
      </footer>
    </div>
  );
}
